import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import MaintenanceMode from './components/MaintenanceMode';
import Home from './pages/Home';
import About from './pages/About';
import Formations from './pages/Formations';
import Boutique from './pages/Boutique';
import Emplacement from './pages/Emplacement';
import Contact from './pages/Contact';
import CookiePolicy from './pages/CookiePolicy';
import FAQ from './pages/FAQ';
import UserRegister from './pages/UserRegister';
import FormationDetail from './pages/FormationDetail';
import PageNotFound from './pages/PageNotFound';
import InscriptionIndividuelle from './pages/InscriptionIndividuelle';
import InscriptionGroupe from './pages/InscriptionGroupe';
import InscriptionPrivee from './pages/InscriptionPrivee';
import DeclarationConfidentialite from './pages/DeclarationConfidentialite';
import ConditionsInscription from './pages/ConditionsInscription';
import Cart from './pages/Cart';
import PaiementReussi from './pages/PaiementReussi';
import PaiementAnnule from './pages/PaiementAnnule';
import { CartProvider } from './contexts/CartContext';
import ChatWidget from './components/ChatWidget';

// Chargé séparément du reste du site : tout le code de l'administration
// (antd, exceljs, xlsx, recharts…) part dans son propre fichier, téléchargé
// seulement par quelqu'un qui va réellement sur /admin — jamais par un
// visiteur du site public.
const AdminApp = React.lazy(() => import('./admin/AdminApp'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#c62828',
    },
    secondary: {
      main: '#2e7d32',
    },
    background: {
      default: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

function MainLayout() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Afficher MaintenanceMode uniquement sur les pages front (pas admin) */}
      {!isAdminPage && <MaintenanceMode />}
      
      {!isAdminPage && <Navbar />}
      <main style={{ 
        flex: 1,
        // Ajouter paddingTop uniquement sur les pages front
        paddingTop: !isAdminPage ? '40px' : '0'
      }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/formations" element={<Formations />} />
          <Route path="/boutique" element={<Boutique />} />
          <Route path="/emplacement" element={<Emplacement />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/politique-cookies" element={<CookiePolicy />} />
          <Route
            path="/admin/*"
            element={
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <CircularProgress sx={{ color: '#c62828' }} />
                  </Box>
                }
              >
                <AdminApp />
              </Suspense>
            }
          />
          <Route path="/register" element={<UserRegister />} />
          <Route path="/formations/:id" element={<FormationDetail />} />
          <Route path="/inscription-individuelle" element={<InscriptionIndividuelle />} />
          <Route path="/inscription-groupe" element={<InscriptionGroupe />} />
          <Route path="/inscription-privee/:token" element={<InscriptionPrivee />} />
          <Route path="/panier" element={<Cart />} />
          <Route path="/paiement-reussi" element={<PaiementReussi />} />
          <Route path="/paiement-annule" element={<PaiementAnnule />} />
          <Route path="/declaration-confidentialite" element={<DeclarationConfidentialite />} />
          <Route path="/conditions-inscription" element={<ConditionsInscription />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </main>
      {!isAdminPage && <Footer />}
      {!isAdminPage && <CookieBanner />}
      {!isAdminPage && <ChatWidget />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CartProvider>
        <Router>
          <MainLayout />
        </Router>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;